import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CalendarSync } from "lucide-react";
import api, { errMsg, money } from "@/lib/api";
import { StageBadge } from "@/components/StageBadge";

const FALLBACK = [
  { key: "unassigned", label: "Unassigned", hint: "No chauffeur yet" },
  { key: "offered", label: "Offered", hint: "Waiting on the chauffeur" },
  { key: "accepted", label: "Accepted", hint: "Chauffeur has the job" },
  { key: "on_road", label: "On the road", hint: "Running now" },
  { key: "done", label: "Done", hint: "Completed or paid" },
  { key: "problem", label: "Problem", hint: "Declined, no-show or cancelled" },
];

export default function Dispatch() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = () =>
      api.get("/dispatch").then(({ data }) => setData(data)).catch((e) => setError(errMsg(e)));
    load();
    window.addEventListener("fm:data-changed", load);
    return () => window.removeEventListener("fm:data-changed", load);
  }, []);

  const lanes = data?.lane_order || FALLBACK;
  const asks = data?.change_requests || 0;

  return (
    <div data-testid="dispatch-page" className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Operations</p>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Dispatch board</h1>
          <p data-testid="dispatch-total" className="mt-2 text-sm text-slate-400">
            {data ? `${data.total} trip${data.total === 1 ? "" : "s"} on the books — every one of them is on this board.` : "Loading…"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/app/dispatch/live" data-testid="dispatch-live-link"
            className="rounded-lg border border-ink-600 px-4 py-2 font-mono text-xs uppercase tracking-widest text-slate-300 hover:border-gold/40 hover:text-gold">
            Live dispatch →
          </Link>
          {data?.pending_bookings > 0 && (
            <Link to="/app/inbox" data-testid="dispatch-pending-bookings"
              className="rounded-lg border border-gold/30 bg-gold/10 px-4 py-2 text-sm text-gold">
              {data.pending_bookings} booking request{data.pending_bookings > 1 ? "s" : ""} waiting →
            </Link>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}

      {asks > 0 && (
        <Link to="/app/inbox" data-testid="dispatch-change-requests"
          className="flex items-center gap-2 rounded-lg border border-amber-400/50 bg-amber-400/10 px-4 py-3 text-sm text-amber-200 transition-colors hover:border-amber-300">
          <CalendarSync className="h-4 w-4 shrink-0" />
          {asks} passenger change request{asks > 1 ? "s" : ""} waiting — approve or decline them in
          Requests →
        </Link>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-6">
        {lanes.map((lane) => (
          <div key={lane.key} data-testid={`lane-${lane.key}`}
            className="rounded-xl border border-ink-600 bg-ink-800 p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="font-mono text-[11px] uppercase tracking-widest text-gold">{lane.label}</p>
              <span data-testid={`lane-count-${lane.key}`} className="font-mono text-xs text-slate-500">
                {(data?.lanes?.[lane.key] || []).length}
              </span>
            </div>
            <p className="mt-1 text-[11px] text-slate-600">{lane.hint}</p>
            <div className="mt-4 space-y-2.5">
              {(data?.lanes?.[lane.key] || []).map((m) => (
                <Link key={m.id} to={`/app/manifests/${m.id}`} data-testid={`lane-card-${m.number}`}
                  className="block rounded-lg border border-ink-600 bg-ink-900/60 p-3 transition-colors hover:border-gold/40">
                  <p className="font-mono text-xs text-gold">#{m.number}</p>
                  <p className="mt-1 truncate text-sm text-slate-100">{m.customer_name}</p>
                  <p className="mt-1 flex items-center gap-1.5 truncate text-[11px] text-slate-500">
                    <CalendarClock className="h-3 w-3" /> {m.trip_date} {m.pickup_time}
                  </p>
                  <p className="mt-1.5 truncate text-[11px] text-slate-400">{m.chauffeur_name || "Unassigned"}</p>
                  <div className="mt-1.5"><StageBadge stage={m.stage || "booked"} testId={`lane-stage-${m.number}`} /></div>
                  {m.change_request && (
                    <p data-testid={`lane-change-request-${m.number}`}
                      className="mt-1.5 flex items-center gap-1.5 rounded border border-amber-400/50 bg-amber-400/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                      <CalendarSync className="h-3 w-3 shrink-0" />
                      Asked for {m.change_request.pickup_time}
                    </p>
                  )}
                  <p className="mt-1 font-mono text-xs text-slate-300">{money(m.total_charge)}</p>
                </Link>
              ))}
              {(data?.lanes?.[lane.key] || []).length === 0 && (
                <p className="py-4 text-center text-xs text-slate-600">Empty</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
